import { type CSSProperties, type ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DND_TRANSITION } from "../../motion.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";

/* ---------------- SortableList ----------------
   Data-blind vertical drag-reorder list on @dnd-kit (the DS's chosen
   drag-reorder engine — strategy §, ADR-0015). Behaviour-only: it renders no
   styled DOM of its own. The host supplies the ordered `ids`, renders one
   <SortableItem> per id, and receives the new order via `onReorder`.

   Pointer + keyboard sensors are both wired, so the drag handle works with the
   mouse AND the keyboard (focus handle → Space to lift → ↑/↓ to move → Space to
   drop). Hosts should keep an explicit control (e.g. up/down buttons) too for
   discoverability — the handle is an enhancement, not the only path. */

export interface SortableListProps {
  /** Stable item ids, in their current order. */
  ids: string[];
  /** The new id order after a pointer- or keyboard-driven reorder. */
  onReorder: (orderedIds: string[]) => void;
  /** One <SortableItem> per id (rendered in `ids` order). */
  children: ReactNode;
}

/**
 * Pure reorder used by `onDragEnd` — moves `activeId` to `overId`'s slot.
 * Returns the input array unchanged when either id is unknown or unchanged, so
 * callers can skip a no-op `onReorder`. Exported for unit coverage.
 */
export function reorderIds(
  ids: string[],
  activeId: string,
  overId: string,
): string[] {
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return ids;
  return arrayMove(ids, from, to);
}

export function SortableList({ ids, onReorder, children }: SortableListProps) {
  const sensors = useSensors(
    // A small activation distance so clicks on the handle (or sibling buttons)
    // don't start a drag; only a deliberate drag does.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over) return;
    const next = reorderIds(ids, String(active.id), String(over.id));
    if (next !== ids) onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

export interface SortableItemRenderProps {
  /** Attach to the item root (the draggable node). */
  setNodeRef: (el: HTMLElement | null) => void;
  /** Transform/transition for the reorder animation — apply to the item root. */
  style: CSSProperties;
  /** Spread on the drag-handle element (carries the a11y attrs + listeners). */
  handleProps: Record<string, unknown>;
  /** True while this item is the one being dragged. */
  isDragging: boolean;
}

export interface SortableItemProps {
  id: string;
  children: (props: SortableItemRenderProps) => ReactNode;
}

export function SortableItem({ id, children }: SortableItemProps) {
  /* dnd-kit settles a reordered row in JavaScript, on its own clock
     (`200ms ease`), so the row moved on a timing the rest of the product does
     not use — and the `prefers-reduced-motion` block in `theme.css` cannot
     reach a transition the library writes inline. `DND_TRANSITION` puts the
     settle on the design system's state grade, and `null` removes it outright
     for a reader who has asked for reduced motion (ADR-0091). */
  const reducedMotion = useReducedMotion();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    transition: reducedMotion ? null : DND_TRANSITION,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 1, position: "relative" } : {}),
  };

  return (
    <>
      {children({
        setNodeRef,
        style,
        // Handle pattern: attrs (role/tabindex/aria-roledescription) + drag
        // listeners both live on the handle element, not the item root.
        handleProps: { ...attributes, ...(listeners ?? {}) },
        isDragging,
      })}
    </>
  );
}
